import { CommonModule } from '@angular/common'
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  Input,
  OnChanges,
  QueryList,
  SimpleChanges,
  ViewChildren,
  WritableSignal,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

import { Store } from '@ngrx/store'
import { last as _last } from 'lodash/fp'

import { startWith } from 'rxjs'

import {
  FeedbackType,
  Message,
  MessageType,
} from '@flowx/designer/modules/new-ai/common/ai.dictionary'
// eslint-disable-next-line import/no-cycle -- pre-existing structural cycle in new-ai module
import { AiEventsApi } from '@flowx/designer/modules/new-ai/common/api/ai-events.api'
import { AiIconsModule } from '@flowx/designer/modules/new-ai/common/icons/ai-icons.module'
import { AiChatBubbleComponent } from '@flowx/designer/modules/new-ai/modules/chat/components/ai-chat-bubble/ai-chat-bubble.component'
import { AiButtonComponent } from '@flowx/designer/modules/new-ai/modules/external/components/ai-action-button/ai-action-button.component'
import { AiAction } from '@flowx/designer/modules/new-ai/modules/external/components/ai-action-button/ai-action-button.types'
import { AiPendingMessageComponent } from '@flowx/designer/modules/new-ai/modules/external/components/ai-pending-message/ai-pending-message.component'
import { AiActions } from '@flowx/designer/modules/new-ai/store/ai.actions'
import { AiSelectors } from '@flowx/designer/modules/new-ai/store/ai.selectors'

enum MessageChangeType {
  New,
  Updating,
  Completed,
}

@Component({
  selector: 'flx-ai-chat-history',
  templateUrl: './chat-history.component.html',
  styleUrls: ['./chat-history.component.scss'],
  imports: [
    CommonModule,
    AiIconsModule,
    AiChatBubbleComponent,
    AiButtonComponent,
    AiPendingMessageComponent,
  ],
})
export class AiChatHistoryComponent implements AfterViewInit, OnChanges {
  #changeDetectorRef = inject(ChangeDetectorRef)
  #store = inject(Store)
  #aiEventsApi = inject(AiEventsApi)
  #destroyRef = inject(DestroyRef)

  @Input() messages: Message[] = []

  @ViewChildren('msgHistory', { read: ElementRef }) msgHistory: QueryList<ElementRef>
  scrollable = viewChild.required<ElementRef>('scrollable')

  aiKeepAction: AiAction = {
    label: 'Keep',
    flavor: 'accept',
    disabled: false,
    icon: 'actions-keep',
    onAction: (): void => this.#store.dispatch(AiActions.keepDraftClick()),
  }

  aiDiscardAction: AiAction = {
    label: 'Discard',
    flavor: 'discard',
    disabled: false,
    icon: 'actions-delete',
    onAction: (): void => this.#store.dispatch(AiActions.discardDraftClick()),
  }

  aiApplyAction: AiAction = {
    label: 'Apply recommendation',
    flavor: 'accept',
    disabled: false,
    icon: 'actions-keep',
    onAction: (): void => this.#store.dispatch(AiActions.applyClick()),
  }

  messages$: WritableSignal<{ history: Message[]; updating: Message }> = signal({
    history: [],
    updating: null,
  })

  loading = this.#store.selectSignal(AiSelectors.pendingAiResponse)

  showPendingMessage = computed(
    () =>
      this.#store.selectSignal(AiSelectors.pendingDocumentsLoading)() ||
      (this.#store.selectSignal(AiSelectors.pendingAiResponse)() &&
        !this.#store.selectSignal(AiSelectors.streamingAiResponse)())
  )

  showAiActionButtons = computed(
    () => this.#store.selectSignal(AiSelectors.hasDraft)() && !this.loading()
  )
  showAuditActionButtons = computed(
    () => this.#store.selectSignal(AiSelectors.hasAudit)() && !this.loading()
  )

  isLastMsgError = this.#store.selectSignal(AiSelectors.isLastMsgError)
  aiDraftActions: AiAction[] = [this.aiKeepAction, this.aiDiscardAction]
  aiAuditActions: AiAction[] = [this.aiApplyAction, this.aiDiscardAction]
  sessionPendingRequestStatus = this.#aiEventsApi.sessionPendingRequestStatus
  pendingRequestStaticMessage = computed(() => {
    const isLoadingDocuments = this.#store.selectSignal(AiSelectors.pendingDocumentsLoading)()
    const isUploadingDocuments = this.#store.selectSignal(AiSelectors.isUploadingDocuments)()
    const documentUploadMessage = this.#store.selectSignal(AiSelectors.documentUploadMessage)()

    if (isLoadingDocuments) {
      return 'Searching for existing documents...'
    } else if (isUploadingDocuments) {
      return documentUploadMessage || 'Uploading and processing document...'
    }
    return 'Working on your request.'
  })
  sessionId = this.#store.selectSignal(AiSelectors.sessionId)

  constructor() {
    effect(() => {
      const loading = this.loading()
      const scrollable = this.scrollable()

      if (loading) {
        this.#scrollToElement(scrollable)
      }
    })
  }

  sendFeedback(message: Message, feedbackType: FeedbackType): void {
    // Most agent handlers null out message.context after rendering, so
    // pull the request_id from the session state as a fallback. lastRequestId
    // tracks the most recent agent generation in this session.
    const requestId =
      message.context?.request_id ?? this.#store.selectSignal(AiSelectors.lastRequestId)()
    this.#store.dispatch(AiActions.feedbackClick({ requestId, feedbackType }))
  }

  cancelPendingRequest(): void {
    this.#store.dispatch(AiActions.cancelPendingRequestClick())
  }

  ngOnChanges(changes: SimpleChanges): void {
    let messageType = null
    if (changes.messages) {
      const { currentValue, previousValue } = changes.messages

      if (currentValue?.length !== previousValue?.length) {
        messageType = MessageChangeType.New
      } else if ((_last(currentValue) as Message)?.sources) {
        messageType = MessageChangeType.Completed
      } else {
        messageType = MessageChangeType.Updating
      }

      if (messageType === MessageChangeType.New || messageType === MessageChangeType.Completed) {
        this.messages$.set({
          history: currentValue ?? [],
          updating: null,
        })
      }

      if (messageType === MessageChangeType.Updating) {
        this.messages$.set({
          history: currentValue.slice(0, -1),
          updating: _last(currentValue),
        })

        this.#changeDetectorRef.detectChanges()
      }
    }
  }

  ngAfterViewInit(): void {
    this.msgHistory.changes
      .pipe(startWith([]), takeUntilDestroyed(this.#destroyRef))
      .subscribe((res) => {
        setTimeout(() => {
          res?.last?.nativeElement.scrollIntoView({ behavior: 'smooth' })
        }, 0)
      })

    const scrollable = this.scrollable()
    this.#scrollToElement(scrollable)
  }

  isUserMessage(message: Message): boolean {
    return message?.type === MessageType.UserMsg
  }

  #scrollToElement(element: ElementRef): void {
    setTimeout(() => {
      element.nativeElement.scrollTo({
        top: element.nativeElement.scrollHeight,
        behavior: 'smooth',
      })
    }, 0)
  }
}
